import { useLazyQuery } from '@apollo/client';
import Cookies from 'js-cookie';
import { useCallback, useEffect } from 'react';

import { UserIdDocument as GET_SCRAMBLED_ID } from './user-id.gql-types';

// V3 Usercentrics API types
type ConsentDetails = {
  consent: {
    status: 'ALL_ACCEPTED' | 'ALL_DENIED' | 'SOME_ACCEPTED' | 'SOME_DENIED';
  };
};

declare global {
  interface Window {
    __ucCmp?: {
      getConsentDetails: () => Promise<ConsentDetails>;
    };
  }
}

const checkAllConsentsAccepted = async (): Promise<boolean> => {
  if (window.__ucCmp?.getConsentDetails) {
    try {
      const consentDetails = await window.__ucCmp.getConsentDetails();
      return consentDetails?.consent.status === 'ALL_ACCEPTED';
    } catch {
      return false;
    }
  }
  return false;
};

export const useScrambledId = (): void => {
  const [fetchScrambledId] = useLazyQuery(GET_SCRAMBLED_ID, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.viewer?.id) {
        Cookies.set('s_id2', data?.viewer?.id, {
          expires: 30,
          domain: 'xing.com',
        });
      }
    },
  });

  const handleInitializedEvent = useCallback((): void => {
    void (async () => {
      try {
        const hasMarketingConsent = await checkAllConsentsAccepted();

        if (Cookies.get('s_id2') && !hasMarketingConsent) {
          Cookies.remove('s_id2', { domain: 'xing.com' });
        }

        if (!Cookies.get('s_id2') && hasMarketingConsent) {
          fetchScrambledId();
        }
      } catch (error) {
        console.error('Failed to get consent details:', error);
      }
    })();
  }, [fetchScrambledId]);

  useEffect(() => {
    window.addEventListener('UC_UI_INITIALIZED', handleInitializedEvent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', handleInitializedEvent);
    };
  }, [handleInitializedEvent]);
};
